import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { Logo } from '@/components/brand/logo'
import { sessionQuery } from '@/lib/session'

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }) => {
    // Signed-in visitors skip the auth screens — except the reset/verify
    // landings, which must work from an email link in any state.
    if (location.pathname.startsWith('/reset-password')) return
    const session = await context.queryClient.fetchQuery(sessionQuery).catch(() => null)
    if (session) throw redirect({ to: '/' })
  },
  component: AuthLayout,
})

function AuthLayout() {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Logo />
        <main className="flex flex-1 items-center py-12">
          <div className="animate-in fade-in slide-in-from-bottom-2 mx-auto w-full max-w-[380px] duration-500 ease-out">
            <Outlet />
          </div>
        </main>
        <p className="text-subtle text-xs">© {new Date().getFullYear()} Nebueye</p>
      </div>
      <BrandPanel />
    </div>
  )
}

/** The one hero moment: the answer Nebueye gives you, floating in its nebula. */
function BrandPanel() {
  return (
    <aside aria-hidden className="nebula relative hidden overflow-hidden lg:block">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,oklch(1_0_0/0.09)_1px,transparent_0)] [background-size:22px_22px]" />
      <div className="relative flex h-full flex-col justify-between p-14 text-[oklch(0.97_0.02_286)]">
        <div className="max-w-md space-y-4 pt-10">
          <p className="text-display font-semibold text-balance">Know what went through.</p>
          <p className="text-[17px] leading-7 text-[oklch(0.9_0.04_286)]">
            Every confirmation, cancel and delivery from your inboxes, sorted into one calm view.
          </p>
        </div>

        <div className="relative ml-auto w-full max-w-sm">
          <div className="absolute -top-28 -left-12 w-56 rotate-[-4deg] rounded-xl bg-[oklch(0.99_0.005_286/0.12)] p-4 backdrop-blur-sm ring-1 ring-[oklch(1_0_0/0.18)]">
            <p className="text-xs text-[oklch(0.9_0.04_286)]">Arriving today</p>
            <p className="text-headline tabular font-semibold">3 packages</p>
          </div>
          <div className="rounded-xl bg-[oklch(0.99_0.004_286)] p-5 text-[oklch(0.2_0.03_286)] shadow-[0_30px_80px_-20px_oklch(0.2_0.15_286/0.6)]">
            <div className="flex items-center justify-between text-xs text-[oklch(0.46_0.026_286)]">
              <span>Pokémon Center · 2h ago</span>
              <span className="rounded-full bg-[oklch(0.955_0.04_160)] px-2 py-0.5 font-medium text-[oklch(0.43_0.11_160)]">Live</span>
            </div>
            <p className="mt-3 text-[15px] font-medium">Prismatic Evolutions ETB</p>
            <p className="text-headline tabular mt-1 font-semibold">
              11 <span className="text-[oklch(0.58_0.02_286)]">of 14 went through</span>
            </p>
            <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-[oklch(0.955_0.03_15)]">
              <div className="h-full w-[78.5%] rounded-full bg-[oklch(0.6_0.14_160)]" />
            </div>
            <div className="mt-3 flex justify-between text-xs text-[oklch(0.46_0.026_286)]">
              <span>11 confirmed</span>
              <span>3 cancelled</span>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
